import React from 'react';
import { Modal } from '@brighterion/br-ui';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { ThresholdSchema } from 'Components/OverallThresholdInput/validation';
import { trackEvent, EventTypes, EventNames } from '@brighterion/insights';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import useUserConfig from 'Hooks/query-hooks/useUserConfig';
import RouteLoading from 'Components/RouteLoading';
import errorController from 'Components/ErrorScreens';
import FieldThreshold from '../FieldThreshold';
import FieldMerchantName from '../FieldMerchantName';
import FieldDescription from '../FieldDescription';
import { MerchantModalProps } from '../types';

import styles from './EditModal.css';

const AddSchema = (showMerchantThreshold: boolean, maxGlobalAggregateThreshold: number) =>
    Yup.object({
        overallThreshold: ThresholdSchema(showMerchantThreshold, maxGlobalAggregateThreshold)
    });

interface Props extends MerchantModalProps<Omit<MerchantFormState, 'id'>> {
    initialValueProp: Omit<MerchantFormState, 'id'>;
    globalThresholdText: string;
    merchantThresholdText: string;
    inheritParameters: string;
    overallThreshold?: number | null;
}

const EditMerchantModal = ({
    isOpen,
    closeModal,
    handleSubmit,
    globalThreshold,
    initialValueProp,
    globalThresholdText,
    merchantThresholdText,
    inheritParameters,
    overallThreshold
}: Props) => {
    const { t } = useTranslation();
    const [showMerchantThreshold, setShowMerchantThreshold] = React.useState(inheritParameters === 'MERCHANT');
    const [fieldThresholdAction, setFieldThresholdAction] = React.useState<FieldThresholdAction>(undefined);
    const { getUserInfo } = useAccessTokenStore.getState();
    const uniqueUserTokenId = getUserInfo().tenantKey;
    const userConfig = useUserConfig(uniqueUserTokenId);

    const handleSubmitForm = (formData: Omit<MerchantFormState, 'id'>) => {
        if (fieldThresholdAction === undefined) {
            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier: 'Subjects - Edit Overview',
                    text: 'Edit Overview'
                }
            });
        } else if (fieldThresholdAction === 'add') {
            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier: 'Subjects - Add Subject Specific Aggregate Scoring Threshold',
                    text: 'Add Subject Specific Aggregate Scoring Threshold'
                }
            });
        } else if (fieldThresholdAction === 'remove') {
            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier: 'Subjects - Remove Subject Specific Aggregate Scoring Threshold',
                    text: 'Remove Subject Specific Aggregate Scoring Threshold'
                }
            });
        }
        handleSubmit(formData);
    };

    if (userConfig.isLoading) {
        return <RouteLoading />;
    }

    if (userConfig.isError) {
        const { errorCmp } = errorController([userConfig.error]);
        return errorCmp;
    }

    return (
        <Modal isOpen={isOpen} closeModal={closeModal} containerClass={styles.modalContainer}>
            <Modal.Title id='edit-global-rules-title' title={t('Edit Merchant Overview')} onClose={closeModal} />
            <Formik
                // Formik will accept "undefined" as an initial value but when we try to do
                // field.setValue(val, undefined), formik deletes the attribute from the values object.
                // therefore, need to transform the undefined value to null so that we can properly check
                // to make sure that the initialValues dont equal the current form values
                initialValues={{ ...initialValueProp, overallThreshold: initialValueProp.overallThreshold ?? null }}
                validationSchema={AddSchema(
                    showMerchantThreshold,
                    userConfig.data.constants.maxGlobalAggregateThreshold
                )}
                onSubmit={handleSubmitForm}
            >
                {({ submitForm, isSubmitting, errors, handleBlur, handleChange, setFieldValue, dirty, isValid }) => {
                    const disableSave = !dirty || isSubmitting || !isValid;
                    return (
                        <div className={styles.formContainer}>
                            <FieldMerchantName handleBlur={handleBlur} handleChange={handleChange} />
                            <FieldDescription handleBlur={handleBlur} handleChange={handleChange} />
                            <FieldThreshold
                                overallThreshold={overallThreshold}
                                globalThreshold={globalThreshold}
                                globalThresholdText={globalThresholdText}
                                merchantThresholdText={merchantThresholdText}
                                errors={errors}
                                setFieldValue={setFieldValue}
                                showMerchantThreshold={showMerchantThreshold}
                                setShowMerchantThreshold={setShowMerchantThreshold}
                                setFieldThresholdAction={setFieldThresholdAction}
                            />
                            <Modal.Footer
                                id='edit-overview-footer'
                                disableOnAction={disableSave}
                                onActionText={t('Save')}
                                onClose={closeModal}
                                onAction={() => {
                                    submitForm();
                                }}
                            />
                        </div>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export default EditMerchantModal;
